// ** React Imports
import React, { useEffect, useState } from 'react';

// ** Third Party Components
import classnames from 'classnames';
import { ChevronUp, Filter } from 'react-feather';
import { Collapse, Card, CardHeader, CardBody, CardTitle, Tooltip } from 'reactstrap';
import Icon from 'utility/icomoon';
import FilterBar from '../FilterBar';
import { getAffiliationById } from '@src/redux/selectors/affiliationsSelectors';
import { FormattedMessage, useIntl } from 'react-intl';
import { appIntl } from '@src/utility/context/IntlGlobalProvider';
import { useSelector } from 'react-redux';

// ** Types
export interface ActionItem {
  title: string | React.ReactElement;
  text: string[];
}

export interface FilterBarOptionsActive {
  nameFilter: string;
  validations?: any[];
  action:()=> ActionItem[] | ActionItem;
}

interface AppCollapseProps {
  data: Array<any>;
  accordion?: boolean;
  blockContent?: boolean;
  borderCard?: boolean;
  type?: 'shadow' | 'border' | 'margin';
  active?: number[] | number;
  titleKey?: string;
  contentKey?: string;
  className?: string;
  style?: React.CSSProperties;
  toggle?: string;
  formValues?: any;
  openCollapse?: number | number[]; // Estado del collapse (qué elementos están abiertos)
  onToggle?: (id: number) => void; // Handler para cuando se hace toggle
  filtersBarOptionsActive?: FilterBarOptionsActive[]; // Nueva prop opcional
}

const AppCollapse = ({
  data,
  type,
  accordion,
  active = [],
  toggle = 'click',
  titleKey,
  contentKey,
  className,
  blockContent,
  borderCard = true,
  style,
  formValues,/*Para poder ver el resumen de filtros seleccinados al colapsar el catalogo de filtros, es necesario enviar el prop de formValues y el de filtersBarOptionsActive*/ 
  openCollapse,/*Para poder manejar el collapse filtros es necesario mandarle este prop openCollapse y onToggle */
  onToggle,
  filtersBarOptionsActive
}: AppCollapseProps) => {
  /**
   ** If accordion is true then return only one active index else return an Array
   */
  const defaultActive = (): number | number[] => {
    if (accordion) {
      return active as number;
    } else {
      return Array.isArray(active) ? [...active] : [];
    }
  };

  // ** State
  const [internalOpenCollapse, setInternalOpenCollapse] = useState<number | number[]>(
    defaultActive()
  );
  const [formValuesList, setFormValuesList] = useState([]);

  // Usar estado controlado si se pasa, sino el interno
  const currentOpenCollapse = openCollapse !== undefined ? openCollapse : internalOpenCollapse;
  const setOpenCollapse = openCollapse !== undefined ? () => {} : setInternalOpenCollapse; // No-op si controlado

  useEffect(() => {
    let newList = [];
    if (filtersBarOptionsActive && Array.isArray(filtersBarOptionsActive)) {
    handleFilterActive(newList);
  }
  }, [formValues]);

  /** Función para manejar los filtros activos y mostrar en el resumen de filtros cuando el collapse esta cerrado */
  const handleFilterActive = (newList) => {
  for (let i = 0; i < filtersBarOptionsActive.length; i++) {
    const nameFilter = filtersBarOptionsActive[i]?.nameFilter;
    const value = formValues[nameFilter];
    if (value !== '' &&
        value !== null &&
        value !== undefined &&
        !Number.isNaN(value) && 
        !filtersBarOptionsActive[i]?.validations.includes(value)
      ) {
      const filterData = filtersBarOptionsActive[i]?.action();
      if(Array.isArray(filterData)){
        newList = [...newList, ...filterData];
      }else if(typeof filterData === "object"){
        newList = [...newList, filterData];
      }
    }
  }
  setFormValuesList(newList);
};



  // ** Function to handle Collapse Toggle
  const handleCollapseToggle = (id: number) => {
    if (onToggle) {
      // Usar función externa si se pasa
      onToggle(id);
    } else {
      // Usar lógica interna si no se pasa
      if (accordion) {
        if (id === currentOpenCollapse) {
          setInternalOpenCollapse(null);
        } else {
          setInternalOpenCollapse(id);
        }
      } else {
        const arr = Array.isArray(currentOpenCollapse) ? currentOpenCollapse : [];
        const index = arr.indexOf(id);
        if (arr.includes(id)) {
          arr.splice(index, 1);
          setInternalOpenCollapse([...arr]);
        } else {
          arr.push(id);
          setInternalOpenCollapse([...arr]);
        }
      }
    }
  };
  const renderData = () => {
    return data.map((item, index) => {
      const title = titleKey ? item[titleKey] : item.title,
        content = contentKey ? item[contentKey] : item.content;

      const isOpen = accordion
        ? currentOpenCollapse === index
        : Array.isArray(currentOpenCollapse)
        ? currentOpenCollapse.includes(index)
        : false;

      return (
        <Card
          id="collapse-filter"
          className={classnames('app-collapse', {
            'border-0': !borderCard,
            [item.className]: item.className,
            open: isOpen && type === 'shadow'
          })}
          key={index}
          style={style}
        >
          <CardHeader
            className={classnames('align-items-center', {
              collapsed: !isOpen
            })}
            /*eslint-disable */
            {...(toggle === 'hover'
              ? {
                  onMouseEnter: () => handleCollapseToggle(index)
                }
              : {
                  onClick: () => handleCollapseToggle(index)
                })}
            /*eslint-enable */
          >
            {currentOpenCollapse === null ? (
              <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                <CardTitle className="collapse-title mb-0" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {title}
                </CardTitle>
                <FilterBar items={formValuesList.length > 0 ? formValuesList : []} />
                <div style={{ display: 'flex', flexShrink: 0, justifyContent: 'end' }}>
                  <ChevronUp size={14} />
                </div>
              </div>
            ) : (
              <>
                <CardTitle className="collapse-title mb-0">{title}</CardTitle>
                <ChevronUp size={14} />
              </>
            )}
          </CardHeader>
          <Collapse isOpen={isOpen}>
            <CardBody>{(!blockContent || isOpen) && content}</CardBody>
          </Collapse>
        </Card>
      );
    });
  };

  useEffect(() => {
    // Solo actualizar estado interno si no está controlado
    if (openCollapse === undefined) {
      const activeLength = Array.isArray(active) ? active.length : active !== undefined ? 1 : 0;
      if (activeLength > 0) {
        setInternalOpenCollapse(defaultActive());
      }
    }
  }, [Array.isArray(active) ? active.length : active, openCollapse]);

  return (
    <div
      className={classnames('collapse-icon', {
        [className]: className,
        'collapse-default': !type,
        'collapse-shadow': type === 'shadow',
        'collapse-border': type === 'border',
        'collapse-margin': type === 'margin'
      })}
    >
      {renderData()}
    </div>
  );
};

export default AppCollapse;
